import { useLayoutEffect, useMemo, useState } from 'react';
import { Icolumn, Irow } from 'react-tailwind-table';
import { format, sub } from 'date-fns';
import { useHistory, useLocation } from 'react-router';
import ROUTES from '../../routes/app.routes-constants';
import { ReferralSummary, formatTextToSlug } from '@ecdlink/core';
import { SearchDropDownOption, Table } from '@ecdlink/ui';
import { useQuery } from '@apollo/client';
import {
  Clinic,
  GetAllPortalClinics,
  GetReferralsSummary,
} from '@ecdlink/graphql';
import { ViewReferralDetailsRouteState } from './view-referral-detail/types';
import { ReferralsRouteState } from './types';

export const Referrals = () => {
  const today = new Date();
  const initialBefore30Days = sub(today, {
    days: 30,
  });
  const minDate = sub(today, {
    months: 6,
  });

  const [dateRange, setDateRange] = useState([initialBefore30Days, today]);
  const [startDateRange, endDateRange] = dateRange;
  const startDate = format(startDateRange, 'yyyy-MM-dd');
  const endDate = format(endDateRange ?? startDateRange, 'yyyy-MM-dd');

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pendingDateRange, setPendingDateRange] = useState([
    initialBefore30Days,
    today,
  ]);
  const [pendingStartDate, pendingEndDate] = pendingDateRange;

  const onChangeDate = (dates) => {
    setPendingDateRange(dates);
  };

  const openDatePicker = () => {
    setPendingDateRange(dateRange);
    setIsDatePickerOpen(true);
  };

  const applyDateFilter = () => {
    setDateRange(pendingDateRange);
    setIsDatePickerOpen(false);
  };

  const cancelDateFilter = () => {
    setIsDatePickerOpen(false);
  };

  const [search, setSearch] = useState<string>('');
  const [selectedClinic, setSelectedClinic] =
    useState<SearchDropDownOption<string>[]>();

  const history = useHistory();

  const { state } = useLocation<ReferralsRouteState>();

  const selectedClinicIds = useMemo(
    () => selectedClinic?.map((clinic) => clinic.value),
    [selectedClinic]
  );

  const { data, loading } = useQuery<{ referralsSummary?: ReferralSummary[] }>(
    GetReferralsSummary,
    {
      variables: {
        startDate,
        endDate,
        clinicIds: selectedClinicIds ?? [],
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: clinicsData, loading: loadingClinics } = useQuery<{
    allPortalClinics?: Clinic[];
  }>(GetAllPortalClinics, {
    fetchPolicy: 'cache-and-network',
  });

  const clinicOptions = clinicsData?.allPortalClinics?.map(
    (clinic): SearchDropDownOption<string> => ({
      id: clinic?.id ?? '',
      label: clinic?.name ?? '',
      value: clinic?.id ?? '',
    })
  );

  const columns: Icolumn[] = [
    {
      field: 'type',
      use: 'Referral type',
    },
    {
      field: 'referralsRaised',
      use: '# referrals raised',
    },
    {
      field: 'referralsMade',
      use: '# referrals made',
    },
    {
      field: 'backReferralsMade',
      use: '# back-referrals made',
    },
  ];

  const sortedReferralsSummary = [...(data?.referralsSummary ?? [])]?.sort(
    (a, b) => b?.referralsRaised - a?.referralsRaised
  );

  const rows: Irow[] =
    (search
      ? sortedReferralsSummary?.filter((item) =>
          item?.type?.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        )
      : sortedReferralsSummary) ?? [];

  const onClearFilters = () => {
    setSearch('');
    setSelectedClinic([]);
    setDateRange([initialBefore30Days, today]);
    setPendingDateRange([initialBefore30Days, today]);
    setIsDatePickerOpen(false);
  };

  useLayoutEffect(() => {
    if (state?.clinicIds && clinicsData?.allPortalClinics?.length > 1) {
      setSelectedClinic(
        clinicsData?.allPortalClinics
          ?.filter((clinic) => state?.clinicIds?.includes(clinic?.id))
          ?.map((clinic) => ({
            id: clinic?.id ?? '',
            label: clinic?.name ?? '',
            value: clinic?.id ?? '',
          }))
      );
    }
  }, [clinicsData?.allPortalClinics, state?.clinicIds]);

  return (
    <div className="bg-adminPortalBg min-h-full rounded-2xl p-4">
      <div className="h-auto rounded-xl bg-white p-12">
        <Table
          rows={rows}
          columns={columns}
          loading={{
            isLoading: loading || loadingClinics,
            size: 'medium',
            spinnerColor: 'adminPortalBg',
            backgroundColor: 'secondary',
          }}
          search={{
            value: search,
            placeholder: 'Search by referral type...',
            onChange: (e) => setSearch(e.target.value),
          }}
          filters={[
            {
              hideFilter: clinicsData?.allPortalClinics?.length <= 1,
              menuItemClassName: 'ml-16',
              type: 'search-dropdown',
              placeholder: 'Clinic',
              overlayTopOffset: '12',
              options: clinicOptions,
              selectedOptions: selectedClinic,
              onChange: setSelectedClinic,
              showSearch: true,
            },
            {
              dateFormat: 'd MMM yyyy',
              className: 'w-64 h-11 mt-1 border-2 border-transparent',
              colour: 'secondary',
              textColour: 'white',
              showChevronIcon: true,
              chevronIconColour: 'white',
              hideCalendarIcon: true,
              isFullWidth: false,
              placeholderText: 'Date range',
              type: 'date-picker',
              selectsRange: true,
              selected: pendingStartDate,
              startDate: pendingStartDate,
              endDate: pendingEndDate,
              minDate,
              maxDate: today,
              onChange: onChangeDate,
              open: isDatePickerOpen,
              onInputClick: openDatePicker,
              onClickOutside: cancelDateFilter,
              shouldCloseOnSelect: false,
              monthsShown: 2,
              calendarClassName: 'referrals-date-range-picker',
              children: (
                <>
                  <div className="w-full"></div>
                  <div className="flex justify-end gap-2 p-2">
                    <button
                      onClick={cancelDateFilter}
                      type="button"
                      className="text-textMid outline-none inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={applyDateFilter}
                      disabled={!pendingStartDate || !pendingEndDate}
                      type="button"
                      className="bg-secondary hover:bg-uiMid outline-none inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Apply
                    </button>
                  </div>
                </>
              ),
            },
          ]}
          onClearFilters={onClearFilters}
          onClickRow={(row) =>
            history.push(
              ROUTES.REFERRALS.VIEW_REFERRAL_DETAIL.ROOT.replace(
                ':referralType',
                row.typeId
              ),
              {
                clinicIds: selectedClinicIds,
                type: row.type,
              } as ViewReferralDetailsRouteState
            )
          }
        />
      </div>
    </div>
  );
};
